import { useEffect, useState, useCallback } from "react";
import { adminApi, TOKEN_KEY } from "@/admin/api";
import { toast } from "sonner";
import { Search, Download, X } from "lucide-react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const STATUS = ["", "paid", "reported", "created", "failed"];
const badge = (s) => ({
  paid: "bg-emerald-100 text-emerald-700", reported: "bg-blue-100 text-blue-700",
  created: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700",
}[s] || "bg-gray-100 text-gray-600");

export default function Donations() {
  const [f, setF] = useState({ search: "", status: "", from: "", to: "", min: "", max: "", payment_id: "", mode: "" });
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const params = { ...f, page, page_size: 25 };
    const { data } = await adminApi.get("/admin/donations", { params });
    setData(data);
  }, [f, page]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await adminApi.get("/admin/donations/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "donations.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const openDetail = async (id) => {
    const { data } = await adminApi.get(`/admin/donations/${id}`);
    setDetail(data);
  };

  const pages = Math.ceil(data.total / 25) || 1;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Donations <span className="text-sm font-normal text-gray-400">({data.total})</span></h1>
        <button data-testid="export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-lg bg-[hsl(160_38%_16%)] text-white px-4 py-2 text-sm font-medium">
          <Download size={15} /> Export CSV
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 grid md:grid-cols-6 gap-3">
        <div className="md:col-span-2 relative">
          <Search size={15} className="absolute left-3 top-2.5 text-gray-400" />
          <input data-testid="filter-search" placeholder="Name, email, phone, payment id" value={f.search}
                 onChange={(e) => { setPage(1); setF({ ...f, search: e.target.value }); }}
                 className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm" />
        </div>
        <select data-testid="filter-status" value={f.status} onChange={(e) => { setPage(1); setF({ ...f, status: e.target.value }); }}
                className="py-2 px-3 rounded-lg border border-gray-300 text-sm">
          {STATUS.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <select data-testid="filter-mode" value={f.mode} onChange={(e) => { setPage(1); setF({ ...f, mode: e.target.value }); }}
                className="py-2 px-3 rounded-lg border border-gray-300 text-sm">
          <option value="">All modes</option><option value="live">Live</option><option value="test">Test</option>
        </select>
        <input data-testid="filter-from" type="date" value={f.from} onChange={(e) => { setPage(1); setF({ ...f, from: e.target.value }); }} className="py-2 px-3 rounded-lg border border-gray-300 text-sm" />
        <input data-testid="filter-to" type="date" value={f.to} onChange={(e) => { setPage(1); setF({ ...f, to: e.target.value }); }} className="py-2 px-3 rounded-lg border border-gray-300 text-sm" />
        <div className="flex gap-2">
          <input data-testid="filter-min" type="number" placeholder="Min ₹" value={f.min} onChange={(e) => { setPage(1); setF({ ...f, min: e.target.value }); }} className="w-full py-2 px-3 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="filter-max" type="number" placeholder="Max ₹" value={f.max} onChange={(e) => { setPage(1); setF({ ...f, max: e.target.value }); }} className="w-full py-2 px-3 rounded-lg border border-gray-300 text-sm" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="donations-table">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
            <tr><th className="p-3">Date</th><th className="p-3">Donor</th><th className="p-3">Amount</th><th className="p-3">Cause</th><th className="p-3">Type</th><th className="p-3">Method</th><th className="p-3">Mode</th><th className="p-3">Status</th></tr>
          </thead>
          <tbody>
            {data.items.map((d) => (
              <tr key={d.id} onClick={() => openDetail(d.id)} data-testid={`donation-row-${d.id}`} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                <td className="p-3 text-xs text-gray-500">{new Date(d.created_at).toLocaleString("en-IN")}</td>
                <td className="p-3"><div className="font-medium text-gray-800">{d.name}</div><div className="text-xs text-gray-400">{d.email}</div></td>
                <td className="p-3 font-semibold text-[hsl(13_65%_44%)]">{inr(d.amount)}</td>
                <td className="p-3">{d.cause}</td>
                <td className="p-3 capitalize">{d.frequency}</td>
                <td className="p-3 uppercase text-xs">{d.method}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${(d.mode || "test") === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{d.mode || "test"}</span></td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge(d.status)}`}>{d.status}</span></td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-gray-400">No donations match your filters.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">Page {page} of {pages}</span>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40">Prev</button>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40">Next</button>
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="donation-detail">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Donation Detail</h2>
              <button onClick={() => setDetail(null)}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[["Donation ID", detail.id], ["Date", new Date(detail.created_at).toLocaleString("en-IN")],
                ["Donor", detail.name], ["Email", detail.email], ["Phone", detail.phone],
                ["Amount", inr(detail.amount)], ["Currency", "INR"], ["Status", detail.status],
                ["Type", detail.frequency], ["Method", detail.method], ["Cause", detail.cause],
                ["Campaign", detail.campaign_id || "—"], ["Razorpay Payment ID", detail.reference || "—"],
                ["Razorpay Order ID", detail.order_id || "—"], ["Refund Status", detail.refund_status || "none"]].map(([k, v]) => (
                <div key={k}><div className="text-xs text-gray-400">{k}</div><div className="text-gray-800 break-all">{v}</div></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
